import { FC, useState, useEffect, useMemo, useContext } from 'react';

import { useQuery } from '@apollo/client';
import { Table } from '@smartshell/ui-kit';
import cn from 'classnames';
import { Column, Row, TableState } from 'react-table';

import { FeatureHOC } from 'features';
import { LocalizationContext } from 'localization';

import s from './DiscountsField.module.scss';
import { GET_AVAILABLE_DISCOUNTS, IDiscountsResponse } from './schema';
import { TDiscount } from './types';

export interface IDiscountFieldValue {
  id: number;
  value: number;
}

interface IDiscountFieldProps {
  value: IDiscountFieldValue | null;
  onChange: (value: IDiscountFieldValue | null) => void;
  disabled?: boolean;
}

const Field: FC<IDiscountFieldProps> = ({ value, onChange, disabled }) => {
  const { t } = useContext(LocalizationContext);

  const COLUMNS: Array<Column<TDiscount>> = useMemo(
    () => [
      {
        Header: t('title'),
        accessor: 'title',
        width: 180,
      },
      {
        Header: t('discount'),
        accessor: 'value',
        Cell: ({ value }) => `${value}%`,
        width: 120,
      },
    ],
    [t]
  );

  const { data, loading } = useQuery<IDiscountsResponse>(
    GET_AVAILABLE_DISCOUNTS,
    {
      fetchPolicy: 'no-cache',
    }
  );

  const [selectedRow, setSelectedRow] = useState<Row<TDiscount> | null>(null);
  const useControlledState = (
    state: TableState<TDiscount>
  ): TableState<TDiscount> => {
    return useMemo(
      () => ({
        ...state,
        selectedRowIds: selectedRow ? { [selectedRow.id]: true } : {},
      }),
      [state, selectedRow?.id]
    );
  };

  useEffect(() => {
    if (!selectedRow) {
      onChange(null);
      return;
    }

    const { id, value } = selectedRow.original;

    onChange({
      id,
      value,
    });
  }, [selectedRow]);

  useEffect(() => {
    if (!value || disabled) {
      setSelectedRow(null);
    }
  }, [value, disabled]);

  return (
    <Table
      className={cn(s['discount-table'], disabled && s.disabled)}
      data={data?.getAvailableDiscounts ?? []}
      columns={COLUMNS}
      onRowClick={(_, row) => {
        if (selectedRow?.id === row.id || disabled) {
          setSelectedRow(null);
          return;
        }

        setSelectedRow(row);
      }}
      useControlledState={useControlledState}
      isLoading={loading}
      disabled={disabled}
    />
  );
};

export const DiscountsField: FC<IDiscountFieldProps> = (props) => (
  <FeatureHOC alias="discounts">
    <Field {...props} />
  </FeatureHOC>
);
